import { motion } from 'framer-motion';
import { useConversationRealtimeNotice } from '../../../hooks/realtime/conversation-realtime-notice';
import { ConversationRealtimeNotice } from '../../molecules/conversation-realtime-notice';
import { useContact } from '../../../hooks/api/contacts';
import { useFunnelConversation } from '../../../hooks/api/funnels/funnel-conversations';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { useContactAddresses } from '../../../hooks/api/contact-addresses';
import { useFunnelAddresses } from '../../../hooks/api/funnels/funnel-addresses';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { ConversationHeader } from './conversation-header';
import { usePhoneContext } from 'src/contexts/phone-context';
import { useGlobalPhoneContext } from 'src/contexts/global-phone-context';
import { ContainerPhone } from 'src/modules/shared/components/organisms/phones/container-phone';
import STATES from 'states/index';
import { useMemo, useEffect, useState, useRef } from 'react';
import { standardizePhoneNumber } from 'src/modules/shared/utils/formatting/phone.util';
import { useGlobalCallProviderContext } from 'src/contexts/global-call-provider-context';
import { ConversationMessageBox } from 'src/modules/communications/components/molecules/conversations/message-box';
import { TimelineItemList } from '../../molecules/timeline-item-list';
import { isTestAddress } from 'src/modules/communications/utils/test-conversation.util';

interface ConversationDetailPanelProps {
  conversationId: number;
  aiStatusIds?: number[];
  onOpenProfile: (contactId: number) => void;
  onClose: () => void;
  showDesktopMetadata?: boolean;
}

export const ConversationDetailPanel: React.FC<
  ConversationDetailPanelProps
> = ({
  conversationId,
  aiStatusIds = [],
  onOpenProfile,
  onClose,
  showDesktopMetadata = true,
}) => {
  const [activeCallId, setActiveCallId] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const flexibleRef = useRef<HTMLDivElement>(null);
  const inlinePanelRef = useRef<HTMLDivElement>(null);
  const heightBoundary = useMemo(
    () => ({
      containerRef: contentRef,
      flexibleRef,
      minimumFlexibleHeight: 48,
    }),
    []
  );

  const {
    conversation,
    loading: loadingConversation,
    error: conversationError,
  } = useFunnelConversation({
    id: conversationId,
  });

  const realtimeNotice = useConversationRealtimeNotice({
    conversationId,
    funnelId: conversation?.funnelId,
  });

  const { contact, loading: loadingContact } = useContact(
    { id: conversation?.contactId ?? 0 },
    !!conversation?.contactId
  );

  const contactAddressesQuery = createQuery()
    .equals('contactId', contact?.id ?? 0)
    .orderByDesc('createdAt')
    .build();
  const { addresses: contactAddresses, loading: loadingAddresses } =
    useContactAddresses(contactAddressesQuery, !!contact?.id);

  const funnelAddressesQuery = createQuery()
    .equals('funnelId', conversation?.funnelId ?? 0)
    .orderByDesc('createdAt')
    .build();
  const { addresses: funnelAddresses, loading: loadingFunnelAddresses } =
    useFunnelAddresses(funnelAddressesQuery, !!conversation?.funnelId);

  const { provider: callProvider, loading: providerLoading } =
    useGlobalCallProviderContext();
  const { setGlobalPhoneEnabled } = useGlobalPhoneContext();
  const { phoneState } = usePhoneContext();

  const shouldShowContainerPhone = useMemo(() => {
    if (providerLoading || !callProvider) return false;
    if (phoneState === STATES.PHONE.READY) return false;
    if (contactAddresses.length === 0) return false;

    const currentNumber =
      phoneState === STATES.PHONE.INCOMING
        ? callProvider.callerNumber
        : callProvider.onCallNumber;

    if (!currentNumber) return false;

    const standardizedCurrentNumber = standardizePhoneNumber(currentNumber);
    if (!standardizedCurrentNumber) return false;

    return contactAddresses.some(
      (addr) =>
        standardizePhoneNumber(addr.address) === standardizedCurrentNumber
    );
  }, [providerLoading, callProvider, phoneState, contactAddresses]);

  useEffect(() => {
    setGlobalPhoneEnabled(!shouldShowContainerPhone);

    return () => {
      setGlobalPhoneEnabled(true);
    };
  }, [shouldShowContainerPhone, setGlobalPhoneEnabled]);

  const isLoading =
    loadingConversation ||
    loadingContact ||
    loadingAddresses ||
    loadingFunnelAddresses;
  const isTestConversation = contactAddresses.some((address) =>
    isTestAddress(address.address)
  );

  if (conversationError) {
    return (
      // TODO: check when implementing i18n
      <ErrorState
        title="Error al cargar la conversación"
        description={conversationError.message}
        actionContent="Cerrar"
        onAction={onClose}
        variant="primary"
      />
    );
  }

  return (
    <motion.div
      ref={containerRef}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      transition={{ duration: 0.2 }}
      className="h-full min-h-0 flex flex-col overflow-hidden bg-white"
    >
      <div
        ref={contentRef}
        className="flex min-h-0 flex-1 flex-col [container-type:size]"
      >
        <ConversationHeader
          isLoading={isLoading}
          contact={contact ?? undefined}
          addresses={contactAddresses}
          funnelId={conversation?.funnelId ?? undefined}
          funnelStatusId={conversation?.funnelStatusId ?? undefined}
          userId={conversation?.userId ?? undefined}
          conversationId={conversationId}
          aiStatusIds={aiStatusIds}
          isTestConversation={isTestConversation}
          onOpenProfile={onOpenProfile}
          onClose={onClose}
          showDesktopMetadata={showDesktopMetadata}
        />

        <ConversationRealtimeNotice state={realtimeNotice} />
        <div ref={flexibleRef} className="flex-1 bg-white min-h-0">
          <Panel
            shadow="inset"
            variant="primary"
            className="relative flex flex-col h-full rounded-none"
          >
            {shouldShowContainerPhone && <ContainerPhone />}
            <TimelineItemList
              layoutRevision={realtimeNotice}
              layoutBoundaryRef={containerRef}
              loadingContact={loadingConversation || loadingContact}
              contact={contact ?? undefined}
              onPlayRecording={setActiveCallId}
              activeCallId={activeCallId}
              funnelId={conversation?.funnelId}
              conversationId={conversationId}
            />
          </Panel>
        </div>

        <div className="shrink-0">
          <ConversationMessageBox
            heightBoundary={heightBoundary}
            inlinePanelContainerRef={inlinePanelRef}
            key={conversationId}
            conversationId={conversationId}
            contactAddresses={contactAddresses}
            funnelAddresses={funnelAddresses}
            isLoading={isLoading}
            isTestConversation={isTestConversation}
          />
        </div>
      </div>
      <div ref={inlinePanelRef} className="shrink-0 empty:hidden" />
    </motion.div>
  );
};
